import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ChevronDown, ChevronUp, Clock, RefreshCw } from 'lucide-react';

import { listAuditLog, type AuditAction, type AuditLogEntry } from '@/api/auditLog.api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

/**
 * Audit log viewer. Two audiences:
 *
 *  1. Quality Lead during change-control review — needs to verify "every
 *     mutation has a reason and an actor." Filter by entity, drill into a
 *     single SOP's timeline.
 *
 *  2. The FDA inspector during the walkthrough — needs to see the trail is
 *     append-only, machine-generated, intelligible. They look for: every
 *     state change carries an actor + IP + reason; the times match the
 *     story the team is telling.
 *
 * The audit log itself is append-only at the database level (Postgres
 * trigger blocking UPDATE/DELETE on `compliance.audit_log`). This page
 * reads it; nothing here can ever modify it.
 */

const PAGE_SIZE = 50;

const ACTION_OPTIONS: { value: '' | AuditAction; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'sign', label: 'Sign' },
  { value: 'approve', label: 'Approve' },
  { value: 'view_sensitive', label: 'View sensitive' },
  { value: 'export', label: 'Export' },
  { value: 'ai_invoke', label: 'AI invocation' },
];

interface FilterState {
  action: '' | AuditAction;
  entityType: string;
  actorEmail: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: FilterState = {
  action: '',
  entityType: '',
  actorEmail: '',
  since: '',
  until: '',
};

export function AuditLogPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);

  const queryArgs = useMemo(
    () => ({
      ...(filters.action ? { action: filters.action } : {}),
      ...(filters.entityType ? { entityType: filters.entityType } : {}),
      ...(filters.actorEmail ? { actorEmail: filters.actorEmail } : {}),
      ...(filters.since ? { since: filters.since } : {}),
      ...(filters.until ? { until: filters.until } : {}),
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, offset],
  );

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ['compliance-audit-log', queryArgs],
    queryFn: () => listAuditLog(queryArgs),
  });

  const hasActiveFilter =
    Boolean(filters.action) ||
    Boolean(filters.entityType) ||
    Boolean(filters.actorEmail) ||
    Boolean(filters.since) ||
    Boolean(filters.until);

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]): void {
    // Filter changes always reset pagination — otherwise the user
    // would be stuck on offset=200 of a brand-new (smaller) result set.
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  }

  function clearAll(): void {
    setFilters(EMPTY_FILTERS);
    setOffset(0);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/compliance/sops"
            className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
          >
            <ArrowLeft className="size-3.5" />
            Back to all SOPs
          </Link>
          <h1 className="typo-h1 mt-1">Audit log</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Append-only trail of every compliance write — actor, action, reason, before/after.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void refetch();
          }}
          isLoading={isFetching && !isLoading}
        >
          <RefreshCw className="size-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
        <FilterField label="Action">
          <select
            value={filters.action}
            onChange={(e) => {
              updateFilter('action', e.target.value as '' | AuditAction);
            }}
            className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Entity type" hint="e.g. sop, audit_binder">
          <input
            type="text"
            value={filters.entityType}
            onChange={(e) => {
              updateFilter('entityType', e.target.value);
            }}
            placeholder="any"
            className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </FilterField>
        <FilterField label="Actor email" hint="substring match">
          <input
            type="text"
            value={filters.actorEmail}
            onChange={(e) => {
              updateFilter('actorEmail', e.target.value);
            }}
            placeholder="e.g. kate"
            className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </FilterField>
        <FilterField label="Since">
          <input
            type="date"
            value={filters.since}
            onChange={(e) => {
              updateFilter('since', e.target.value);
            }}
            className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </FilterField>
        <FilterField label="Until">
          <input
            type="date"
            value={filters.until}
            onChange={(e) => {
              updateFilter('until', e.target.value);
            }}
            className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </FilterField>
        {hasActiveFilter ? (
          <button
            type="button"
            onClick={clearAll}
            className="col-span-full text-left text-[12px] text-kw-cerulean hover:text-kw-cerulean-dark sm:col-span-2 lg:col-span-5"
          >
            Clear filters
          </button>
        ) : null}
      </Card>

      {/* Body */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load the audit log</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : !data || data.data.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Clock className="size-10 text-kw-ghost" />
          <p className="text-[14px] text-kw-muted">
            {hasActiveFilter ? 'No audit-log rows match the current filters.' : 'No audit-log activity yet.'}
          </p>
        </Card>
      ) : (
        <>
          <div className="text-[12px] text-kw-muted">
            Showing rows <strong>{String(offset + 1)}</strong>–
            <strong>{String(Math.min(offset + data.data.length, data.total))}</strong> of{' '}
            <strong>{String(data.total)}</strong>.
          </div>
          <Card className="p-0">
            {/* `overflow-clip` (not hidden) lets the sticky thead stay in
                position as the user scrolls the page — `overflow-hidden`
                would create a new containing block that breaks
                position:sticky. */}
            <div className="overflow-clip rounded-kw-md">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 z-10 bg-kw-bg text-left text-[11px] uppercase tracking-wide text-kw-muted shadow-kw-xs">
                  <tr>
                    <th className="w-[150px] px-3 py-2">When</th>
                    <th className="px-3 py-2">Actor</th>
                    <th className="w-[130px] px-3 py-2">Action</th>
                    <th className="px-3 py-2">Entity</th>
                    <th className="px-3 py-2">Reason</th>
                    <th className="w-[40px] px-3 py-2" aria-label="Expand" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-kw-border">
                  {data.data.map((row) => (
                    <Row key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => {
                setOffset(Math.max(0, offset - PAGE_SIZE));
              }}
            >
              ← Previous
            </Button>
            <span className="text-[12px] text-kw-muted">
              Page {String(Math.floor(offset / PAGE_SIZE) + 1)} of{' '}
              {String(Math.max(1, Math.ceil(data.total / PAGE_SIZE)))}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + data.data.length >= data.total}
              onClick={() => {
                setOffset(offset + PAGE_SIZE);
              }}
            >
              Next →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">
        {label}
        {hint ? <span className="ml-1 normal-case text-kw-ghost">— {hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function Row({ row }: { row: AuditLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetail =
    row.beforeJson !== null ||
    row.afterJson !== null ||
    row.reasonForChange !== null ||
    row.signatureMeaning !== null;

  return (
    <>
      <tr
        className="cursor-pointer align-top hover:bg-kw-bg-blue"
        onClick={() => {
          if (hasDetail) setOpen(!open);
        }}
      >
        <td className="px-3 py-2 font-mono text-[11px] text-kw-muted">
          {new Date(row.occurredAt).toISOString().replace('T', ' ').slice(0, 19)}Z
        </td>
        <td className="px-3 py-2">
          <p className="text-kw-shark">{row.actorNameSnapshot}</p>
          <p className="text-[11px] text-kw-muted">
            {row.actorEmailSnapshot} ·{' '}
            <span className="capitalize">{row.actorRoleSnapshot.replace('_', ' ')}</span>
          </p>
        </td>
        <td className="px-3 py-2">
          <ActionBadge action={row.action} />
        </td>
        <td className="px-3 py-2">
          <p className="font-mono text-[12px] text-kw-shark">{row.entityType}</p>
          {row.entityId ? <p className="font-mono text-[11px] text-kw-muted">{row.entityId}</p> : null}
        </td>
        <td className="px-3 py-2 text-kw-shark">
          {row.reasonForChange ?? row.signatureMeaning ?? <span className="text-kw-ghost">—</span>}
        </td>
        <td className="px-3 py-2 text-right">
          {hasDetail ? (
            open ? (
              <ChevronUp className="size-4 text-kw-muted" />
            ) : (
              <ChevronDown className="size-4 text-kw-muted" />
            )
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="bg-kw-bg/40">
          <td colSpan={6} className="px-3 py-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <JsonBlock title="Before" payload={row.beforeJson} />
              <JsonBlock title="After" payload={row.afterJson} />
            </div>
            {row.ipAddress || row.userAgent ? (
              <p className="mt-2 text-[11px] text-kw-muted">
                {row.ipAddress ? <span>IP {row.ipAddress}</span> : null}
                {row.ipAddress && row.userAgent ? ' · ' : ''}
                {row.userAgent ? <span>UA {row.userAgent}</span> : null}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function JsonBlock({ title, payload }: { title: string; payload: unknown }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-kw-muted">{title}</p>
      <pre className="max-h-[280px] overflow-auto rounded-kw-md border border-kw-border bg-white p-2 font-mono text-[11px] text-kw-shark">
        {payload === null || payload === undefined ? '—' : JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  );
}

function ActionBadge({ action }: { action: AuditAction }) {
  const styles: Record<AuditAction, string> = {
    create: 'bg-kw-emerald-light text-kw-tag-text',
    update: 'bg-kw-cerulean-light text-kw-cerulean-dark',
    delete: 'bg-kw-danger-bg text-kw-danger-text',
    sign: 'bg-kw-warning-bg text-kw-warning-text',
    approve: 'bg-kw-emerald-light text-kw-tag-text',
    view_sensitive: 'bg-kw-bg text-kw-muted',
    export: 'bg-kw-bg-blue text-kw-cerulean-dark',
    ai_invoke: 'bg-kw-bg text-kw-muted',
  };
  const labels: Record<AuditAction, string> = {
    create: 'Create',
    update: 'Update',
    delete: 'Delete',
    sign: 'Sign',
    approve: 'Approve',
    view_sensitive: 'View sensitive',
    export: 'Export',
    ai_invoke: 'AI invoke',
  };
  return (
    <span className={`inline-block rounded-kw-full px-2 py-0.5 text-[11px] font-medium ${styles[action]}`}>
      {labels[action]}
    </span>
  );
}
